import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { Observable } from 'rxjs';
import {
    ICmsCategory,
    ICmsContentPage,
    ICmsImageResponse,
    ICmsLanguage,
    ICmsPort,
    IUpdatedCmsContentPage
} from '../interfaces/cms.interface';

@Injectable({
    providedIn: 'root'
})
export class CmsService {
    http = inject(HttpClient);
    baseUrl = environment.apiUrl;

    /**
     * Get all CMS categories
     * @returns Observable<ICmsCategory[]> - List of all categories
     */
    categoriesGetAll(): Observable<ICmsCategory[]> {
        const apiUrl = `${this.baseUrl}/cms/categories`;
        return this.http.get<ICmsCategory[]>(apiUrl);
    }

    /**
     * Get all available languages
     * @returns Observable<ICmsLanguage[]> - List of all languages
     */
    languagesGetAll(): Observable<ICmsLanguage[]> {
        const apiUrl = `${this.baseUrl}/cms/languages`;
        return this.http.get<ICmsLanguage[]>(apiUrl);
    }

    /**
     * Get all CMS content pages
     * @returns Observable<ICmsContentPage[]> - List of all content pages
     */
    contentPagesGetAll(): Observable<ICmsContentPage[]> {
        const apiUrl = `${this.baseUrl}/cms/contentPages`;
        return this.http.get<ICmsContentPage[]>(apiUrl);
    }

    /**
     * Get CMS content page by ID
     * @param id - Content page ID
     * @returns Observable<ICmsContentPage> - Content page detail
     */
    contentPagesGetById(id: number): Observable<ICmsContentPage> {
        const apiUrl = `${this.baseUrl}/cms/contentPages/${id}`;
        return this.http.get<ICmsContentPage>(apiUrl);
    }

    /**
     * Create a new CMS content page
     * @param contentPage - Content page data to create
     * @returns Observable<ICmsContentPage> - Created content page
     */
    contentPageCreate(contentPage: IUpdatedCmsContentPage): Observable<ICmsContentPage> {
        const apiUrl = `${this.baseUrl}/cms/contentPages`;
        return this.http.post<ICmsContentPage>(apiUrl, contentPage);
    }

    /**
     * Update an existing CMS content page
     * @param id - Content page ID to update
     * @param contentPage - Updated content page data
     * @returns Observable<ICmsContentPage> - Updated content page
     */
    contentPageUpdate(id: number | null, contentPage: IUpdatedCmsContentPage): Observable<ICmsContentPage> {
        const apiUrl = `${this.baseUrl}/cms/contentPages/${id}`;
        return this.http.put<ICmsContentPage>(apiUrl, contentPage);
    }

    /**
     * Delete an image by ID
     * @param id - Image ID to delete
     * @returns Observable<void> - Empty response on successful deletion
     */
    deleteImage(id: number): Observable<void> {
        const apiUrl = `${this.baseUrl}/cms/images/${id}`;
        return this.http.delete<void>(apiUrl);
    }

    /**
     * Delete a CMS content page by ID
     * @param id - Content page ID to delete
     * @returns Observable<void> - Empty response on successful deletion
     */
    contentPageDelete(id: number): Observable<void> {
        const apiUrl = `${this.baseUrl}/cms/contentPages/${id}`;
        return this.http.delete<void>(apiUrl);
    }

    getPorts(): Observable<ICmsPort[]> {
        const apiUrl = `${this.baseUrl}/ports`;
        return this.http.get<ICmsPort[]>(apiUrl);
    }

    uploadOgImage(contentPageId: number, file: File): Observable<ICmsImageResponse> {
        const apiUrl = `${this.baseUrl}/cms/contentPages/${contentPageId}/og-image`;
        const formData = new FormData();
        formData.append('file', file);
        return this.http.post<ICmsImageResponse>(apiUrl, formData);
    }

    deleteOgImage(contentPageId: number): Observable<void> {
        const apiUrl = `${this.baseUrl}/cms/contentPages/${contentPageId}/og-image`;
        return this.http.delete<void>(apiUrl);
    }

    uploadSearchBgImage(contentPageId: number, file: File): Observable<ICmsImageResponse> {
        const apiUrl = `${this.baseUrl}/cms/contentPages/${contentPageId}/search-bg-image`;
        const formData = new FormData();
        formData.append('file', file);
        return this.http.post<ICmsImageResponse>(apiUrl, formData);
    }

    deleteSearchBgImage(contentPageId: number): Observable<void> {
        const apiUrl = `${this.baseUrl}/cms/contentPages/${contentPageId}/search-bg-image`;
        return this.http.delete<void>(apiUrl);
    }
}
